"use strict";
/**
 * Limits for `/api/rss` to avoid serverless timeouts, OOM from huge XML/JSON,
 * and oversized responses. Tune via env on the host if needed.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.rssUrlItemLimit = rssUrlItemLimit;
exports.pubmedRecordLimit = pubmedRecordLimit;
exports.rssUrlDescriptionMaxChars = rssUrlDescriptionMaxChars;
exports.pubmedAbstractMaxChars = pubmedAbstractMaxChars;
exports.genericRssMaxCachedItems = genericRssMaxCachedItems;
exports.pubmedIncrementalMaxPmidList = pubmedIncrementalMaxPmidList;
exports.rssMemoryCacheTtlMs = rssMemoryCacheTtlMs;
exports.rssMemoryCacheMaxKeys = rssMemoryCacheMaxKeys;
exports.rssMemoryCacheStaleMs = rssMemoryCacheStaleMs;
exports.rssFeedOffset = rssFeedOffset;
exports.rssIncrementalBatchLimit = rssIncrementalBatchLimit;
exports.trimGenericRssItemDescriptions = trimGenericRssItemDescriptions;
function intEnv(name, fallback) {
    const v = process.env[name];
    if (v == null || v === "")
        return fallback;
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
}
/** Max items returned when proxying a generic RSS URL (after parse). */
function rssUrlItemLimit(requested) {
    const raw = Array.isArray(requested) ? requested[0] : requested;
    const parsed = Number.parseInt(raw ?? "", 10);
    const def = intEnv("RSS_API_URL_ITEMS_DEFAULT", 200);
    const cap = intEnv("RSS_API_URL_ITEMS_MAX", 400);
    if (!Number.isFinite(parsed) || parsed < 1)
        return def;
    return Math.min(parsed, cap);
}
/** Max PubMed records per request (E-utilities time + payload). */
function pubmedRecordLimit(requested) {
    const raw = Array.isArray(requested) ? requested[0] : requested;
    const parsed = Number.parseInt(raw ?? "", 10);
    const def = intEnv("RSS_API_PUBMED_DEFAULT", 150);
    const cap = intEnv("RSS_API_PUBMED_MAX", 250);
    if (!Number.isFinite(parsed) || parsed < 1)
        return def;
    return Math.min(parsed, cap);
}
/** Truncate long description/body on generic RSS items (chars, UTF-16). */
function rssUrlDescriptionMaxChars() {
    return intEnv("RSS_API_DESCRIPTION_MAX_CHARS", 600);
}
/** Truncate PubMed abstract text in the API response. 0 = no truncation. */
function pubmedAbstractMaxChars() {
    return intEnv("RSS_API_PUBMED_ABSTRACT_MAX_CHARS", 520);
}
/** Max items kept in memory after parsing a generic RSS URL (incremental cache). */
function genericRssMaxCachedItems() {
    return intEnv("RSS_API_GENERIC_MAX_CACHED_ITEMS", 3000);
}
/**
 * Max PubMed PMIDs to resolve per search for incremental loading (ESearch retmax cap).
 * Larger = more background batches possible; higher NCBI + memory cost.
 */
function pubmedIncrementalMaxPmidList() {
    return intEnv("RSS_API_PUBMED_INCREMENTAL_MAX_PMIDS", 5000);
}
/** TTL for in-process RSS / PMID caches (ms). */
function rssMemoryCacheTtlMs() {
    return intEnv("RSS_API_MEMORY_CACHE_TTL_MS", 10 * 60 * 1000);
}
/** Max distinct keys in in-process RSS / PMID caches. */
function rssMemoryCacheMaxKeys() {
    return intEnv("RSS_API_MEMORY_CACHE_MAX_KEYS", 40);
}
/** How long expired in-process cache entries may still be served after upstream failure (ms). */
function rssMemoryCacheStaleMs() {
    return intEnv("RSS_API_MEMORY_CACHE_STALE_MS", 24 * 60 * 60 * 1000);
}
function rssFeedOffset(requested) {
    const raw = Array.isArray(requested) ? requested[0] : requested;
    const n = Number.parseInt(raw ?? "0", 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}
/**
 * Batch size for incremental requests. Uses `limit` query (generic); PubMed also accepts `maxRecords`.
 */
function rssIncrementalBatchLimit(limitRaw, maxRecordsRaw, pubmed) {
    if (pubmed) {
        return pubmedRecordLimit(limitRaw ?? maxRecordsRaw);
    }
    return rssUrlItemLimit(limitRaw);
}
const RSS_ITEM_DESC_KEYS = [
    "description",
    "body",
    "summary",
    "content:encoded",
];
/** Shorten long fields on generic RSS items to shrink JSON payloads. */
function trimGenericRssItemDescriptions(items, maxChars) {
    if (maxChars <= 0)
        return items;
    return items.map((raw) => {
        const o = { ...raw };
        for (const key of RSS_ITEM_DESC_KEYS) {
            const v = o[key];
            if (typeof v === "string" && v.length > maxChars) {
                o[key] = `${v.slice(0, maxChars).trimEnd()}…`;
            }
        }
        return o;
    });
}
